import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  Directive,
  effect,
  ElementRef,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { Icon } from '../icon/icon';

let nextId = 0;

/**
 * Modal — Figma's `Modal` (node 311:3676). Six `Type` variants — Info, Payment,
 * Log in, Invite a Friend, Update and **Slot** — which are six pieces of content in
 * one shell, not six components. Figma shipping a `Slot` type says as much.
 *
 * ```html
 * <ui-modal [(open)]="confirming" heading="Delete this post?"
 *           description="This can't be undone.">
 *   <div uiModalActions>
 *     <button ui-button destructive (click)="remove()">Delete</button>
 *     <button ui-button variant="secondary" (click)="confirming.set(false)">Cancel</button>
 *   </div>
 * </ui-modal>
 * ```
 *
 * **It is a native `<dialog>` opened with `showModal()`**, which is the whole reason
 * this component is small: the platform gives the backdrop, the focus trap, Escape
 * to dismiss, the top layer — so a modal inside a transformed or `overflow:hidden`
 * ancestor still covers the page — and inertness for everything behind it. None of
 * that is reimplemented here.
 *
 * `open` is two-way, so Escape and the close button report back rather than leaving
 * the caller's flag lying about what's on screen.
 *
 * **It never grows past the viewport.** The panel stops a margin short of the
 * screen's height and the body — everything projected without `uiModalActions` —
 * scrolls inside it, while the heading, the close button and the actions slot stay
 * put. A long form therefore keeps its Save reachable at any length, as long as the
 * buttons go in `uiModalActions` rather than in the body.
 */
@Component({
  selector: 'ui-modal',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './modal.scss',
  host: { class: 'ui-modal' },
  template: `
    <dialog
      #dialog
      class="ui-modal__panel"
      [class.ui-modal__panel--md]="size() === 'md'"
      [class.ui-modal__panel--lg]="size() === 'lg'"
      [attr.aria-labelledby]="heading() ? headingId : null"
      (close)="open.set(false)"
    >
      @if (dismissible()) {
        <button
          class="ui-modal__close"
          type="button"
          [attr.aria-label]="closeLabel()"
          (click)="dismiss()"
        >
          <ui-icon name="x" />
        </button>
      }

      @if (heading() || description()) {
        <div class="ui-modal__text">
          @if (heading()) {
            <h2 class="ui-modal__heading" [id]="headingId">{{ heading() }}</h2>
          }
          @if (description()) {
            <p class="ui-modal__description">{{ description() }}</p>
          }
        </div>
      }

      <div class="ui-modal__body">
        <ng-content />
      </div>

      <ng-content select="[uiModalActions]" />
    </dialog>
  `,
})
export class Modal {
  /** Two-way: Escape and the close button set it back to false. */
  readonly open = model(false);

  /** Figma's `Medium length headline` — a Subheading, and the dialog's label. */
  readonly heading = input<string>();

  /** The body line under it. Anything richer goes in the content slot. */
  readonly description = input<string>();

  /**
   * Panel width. `sm` is Figma's one modal width (400px) and the default; `md`
   * (560px) and `lg` (720px) are derived steps for content the 400px panel keeps
   * defeating — multi-field forms and pickers were reaching for native <dialog>
   * escape hatches or growing horizontal scrollbars instead. Not yet drawn in
   * Figma (node 311:3676 has a single width); flagged for the design file.
   */
  readonly size = input<'sm' | 'md' | 'lg'>('sm');

  /** Figma's `showDisclosureButton`. */
  readonly dismissible = input(true, { transform: booleanAttribute });

  readonly closeLabel = input('Close');

  /** Fires whenever the modal closes, however it was closed. */
  readonly closed = output<void>();

  protected readonly headingId = `ui-modal-${nextId++}`;

  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  constructor() {
    effect(() => {
      const dialog = this.dialog().nativeElement;
      if (this.open()) {
        // showModal() rather than show(): the modal behaviour — backdrop, focus trap,
        // Escape, inert background — comes with that one and not the other.
        if (!dialog.open) dialog.showModal();
      } else if (dialog.open) {
        dialog.close();
      }
    });

    // `close` fires for Escape as well as for close(), so this is the one place that
    // needs to report the modal is gone.
    effect((onCleanup) => {
      const dialog = this.dialog().nativeElement;
      const onClose = () => this.closed.emit();
      dialog.addEventListener('close', onClose);
      onCleanup(() => dialog.removeEventListener('close', onClose));
    });
  }

  protected dismiss(): void {
    this.open.set(false);
  }
}

/**
 * The actions row — Figma's `Additional`: full-width buttons stacked 8px apart, the
 * primary above the secondary. That is a phone-shaped pattern rather than the usual
 * desktop row, and it is what the design draws at every one of the six types.
 *
 * A directive rather than a wrapper in the modal's template, because the element is
 * the caller's: projected content keeps the consumer's style encapsulation, so a
 * rule in modal.scss would style the slot but never what they put in it.
 */
@Directive({
  selector: '[uiModalActions]',
  host: {
    class: 'ui-modal__actions',
    style: 'display:flex; flex-direction:column; align-items:stretch; gap:0.5rem; width:100%',
  },
})
export class ModalActions {}
